import React, { forwardRef, MutableRefObject, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import UserDropdown from '../BotBuilderSidebar/UserDropdown/UserDropdown';
import { PageSwitcher } from '../BotBuilderSidebar/SidebarHeader/PageSwitcher/PageSwitcher';
import SendLimitAlert from '../BotBuilderSidebar/SidebarHeader/SendLimitAlert/SendLimitAlert';
import { AutomationTemplateContainer } from '../../editor/features/beta/modules/Automations/AutomationHome/TemplateIndex/AutomationTemplateContainer';
import { TemplateGoButton } from '../../editor/features/beta/modules/Automations/AutomationHome/TemplateIndex/TemplateGoButton';
import {
  CloseOutlined,
  LeftOutlined,
  MenuOutlined,
  PlusOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { PrimaryButton } from '../../common/buttons/PrimaryButton/PrimaryButton';
import { BotBuilderRoutes } from '../../routes/bot-builder-routes';
import { useHistory } from 'react-router-dom';
import { getPlanInfo } from '@mm/state/helpers/billing-info-helpers';
import { isBilling2023Subscription } from '@mm/state/model/SubscriptionModel';
import { UserRoleContainer } from '../../common/hooks/use-route-guard';
import styles from './TopHeader.module.css';
import { FreeTrialCountdown } from '../BotBuilderSidebar/FreeTrialCountdown/FreeTrialCountdown';
import { trackWebAppGAEvent } from '../../common/helpers/plugins/website-google-analytics';
import {
  activeChatbotSelector,
  selectIsShopifyContext,
} from '@mm/state/selectors/viewSelectors';
import { Logo } from '../BotBuilderSidebar/Logo/Logo';
import chatbotSidebarActions from '@mm/state/modules/chatbotSidebarState/actions';
import { StoreState } from '@mm/state/store-interfaces';
import { useViewActions } from '@mm/state/actions/viewActions';
import { useResolutionInfo } from '@mm/hooks';
import {
  MOBILE_SMALL,
  SIDEBAR_THRESHOLD,
} from '../../editor/constants/screenSize';
import { filteredSidebarMenuSelector } from '../filteredSidebarMenuSelector';
import { Button } from 'antd';
import { shopifyRoutes } from '../../modules/Shopify/constants';
import { useTokenLogin } from '../../modules/Shopify/use-token-login';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';

type Props = {
  facebookPage: any;
  activeChatbotId: number;
  isFreeTrialActive: boolean;
};

export const TopHeader = forwardRef(
  (props: Props, ref: MutableRefObject<HTMLDivElement | null>) => {
    const { facebookPage, activeChatbotId, isFreeTrialActive } = props;
    const chatbot = useSelector(activeChatbotSelector);
    const isShopify = useSelector(selectIsShopifyContext);
    const filteredSidebarMenu = useSelector(filteredSidebarMenuSelector);
    const featureFlags = useSelector(combinedFeatureFlagsSelector);
    const login = useTokenLogin();
    const dispatch = useDispatch();
    const sidebarMinimized = useSelector(
      (state: StoreState) => state.chatbotSidebarState.isMinimized,
    );
    const setSidebarMinimized = (isMinimized: boolean) =>
      dispatch(chatbotSidebarActions.setIsMinimized(isMinimized));

    const viewActions = useViewActions();
    const openSlideoutPageMenu = () => {
      viewActions.showSlideoutMenu('pages', {});
    };

    const { screenWidth } = useResolutionInfo();
    const showAbsoluteSidebar = screenWidth < SIDEBAR_THRESHOLD;
    const isMobile = screenWidth < MOBILE_SMALL;

    const { pixelName = 'X-Ray Pixel' } = useWhitelabelConfig();

    const {
      roleFlags: { isLimitedRole },
    } = UserRoleContainer.useContainer();

    const planInfo = getPlanInfo(facebookPage);
    const history = useHistory();

    const hideSendLimitAlert =
      planInfo.isCustomTeam ||
      planInfo.isLegacyTeamPlan ||
      (facebookPage?.subscription &&
        isBilling2023Subscription(facebookPage.subscription));

    let logoHref = '';

    if (chatbot?.id && !isLimitedRole) {
      logoHref = BotBuilderRoutes.dashboard.getLink({
        chatbotId: chatbot.id,
      });
    }

    const onLogoClick = () => {
      trackWebAppGAEvent('click', {
        category: 'navigation',
        label: 'navbar_logo',
      });
    };

    return (
      <div className={styles.container}>
        <div className={styles.logo}>
          {!!filteredSidebarMenu.length && showAbsoluteSidebar && (
            <div
              ref={ref}
              className={styles.logoIcons}
              onClick={() => {
                setSidebarMinimized(!sidebarMinimized);
              }}
            >
              {sidebarMinimized ? (
                <MenuOutlined className={styles.closeIcon} />
              ) : (
                <CloseOutlined className={styles.closeIcon} />
              )}
            </div>
          )}
          {!isMobile && <Logo href={logoHref} onClick={onLogoClick} />}
        </div>
        {!isMobile && (
          <FreeTrialCountdown
            withBenefits={false}
            className={styles.freeTrial}
          />
        )}
        {isShopify ? (
          <div className={styles.shopifyButtons}>
            <Button
              className={styles.shopifyBillingLink}
              onClick={() => history.push(shopifyRoutes.billing.path)}
              type="text"
            >
              Billing
            </Button>
            <div className={styles.shopifyMenu}>
              <Button onClick={login.logout}>Logout</Button>
            </div>
          </div>
        ) : (
          <div className={styles.right}>
            {!hideSendLimitAlert && <SendLimitAlert />}
            {isLimitedRole ? null : !facebookPage?.getemailsActivated ? (
              <PrimaryButton
                className={styles.addAutomationButton}
                onClick={() =>
                  history.push(
                    BotBuilderRoutes.xRay.getLink({
                      chatbotId: activeChatbotId,
                    }),
                  )
                }
              >
                Install {pixelName}
              </PrimaryButton>
            ) : featureFlags.compliant_klaviyo_app ? null : (
              <PrimaryButton
                className={styles.addAutomationButton}
                onClick={() =>
                  history.push(
                    BotBuilderRoutes.integrations.getLink({
                      chatbotId: activeChatbotId,
                    }),
                  )
                }
              >
                Connect CRM
              </PrimaryButton>
            )}

            <PageSwitcher
              facebookPage={facebookPage}
              onMenuClick={openSlideoutPageMenu}
              isMobile={isMobile}
            />
            <UserDropdown isFreeTrialActive={isFreeTrialActive} />
          </div>
        )}
      </div>
    );
  },
);
